// Nazorg adopter portal — pure, React-free route helpers (unit-testable without a DOM).
//
// The adopter reaches the aftercare questionnaire through a tokenised link in mail/WhatsApp
// (`/nazorg/<token>`). The token rides in the PATH (not a fragment like /set-password): it is
// single-use and identifies the invitation, and the page is a standalone unauthenticated landing —
// it must NOT run the auth gate (no silent restore, no login redirect). The token lives in component
// state only; the address bar is cleaned to `/nazorg` after capture so a copied URL can't be reused.

const NAZORG_PREFIX = "/nazorg";

/** True when the current path is the nazorg portal route (`/nazorg` or `/nazorg/<token>`). */
export function isNazorgPath(pathname: string | null | undefined): boolean {
  if (!pathname) {
    return false;
  }
  const path = pathname.replace(/\/+$/, "");
  return path === NAZORG_PREFIX || path.startsWith(`${NAZORG_PREFIX}/`);
}

/**
 * Extract the invitation token from a `/nazorg/<token>` path. Returns the trimmed token, or `""` when
 * there is none. Takes the pathname as an argument (not `location`) so it is testable in isolation.
 * The `index.html` segment (a static-host style link like `/nazorg/index.html?token=…`) is NOT a
 * token — the query param carries it in that shape; see {@link readNazorgToken}.
 */
export function readNazorgTokenFromPath(pathname: string | null | undefined): string {
  if (!pathname) {
    return "";
  }
  const match = pathname.replace(/\/+$/, "").match(/^\/nazorg\/(.+)$/);
  if (!match || match[1] === "index.html") {
    return "";
  }
  try {
    return decodeURIComponent(match[1]).trim();
  } catch {
    return match[1].trim();
  }
}

/** Read the `token` query parameter (e.g. `?token=abc`). Returns the trimmed value or `""`. */
export function readNazorgTokenFromQuery(search: string | null | undefined): string {
  if (!search) {
    return "";
  }
  try {
    const token = new URLSearchParams(search).get("token");
    return token ? token.trim() : "";
  } catch {
    return "";
  }
}

/**
 * Resolve the invitation token from either the path (`/nazorg/<token>`) or the `?token=` query — so the
 * portal accepts both the platform's own path-style link AND a backoffice-style
 * `…/nazorg/index.html?token=<token>` link without the caller having to normalise the URL shape. The
 * query wins when both are present.
 */
export function readNazorgToken(
  pathname: string | null | undefined,
  search: string | null | undefined,
): string {
  return readNazorgTokenFromQuery(search) || readNazorgTokenFromPath(pathname);
}
